import { useEffect, useMemo, useState } from "react";
import { waeAPI, OP_LABEL } from "./waeApi";
import { ArrowRight, CheckCircle2, Layers } from "lucide-react";
import { EmptyState } from "./uiKit";

const KIND_LABEL = {
  user: "User", role: "Role", job_title: "Title",
  department_head: "Dept Head", capability: "Capability",
  requester_manager: "Manager",
};

export default function WAEChains() {
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    waeAPI.approvalTypes().then(setTypes);
    waeAPI.templates().then(setTemplates);
  }, []);

  const byType = useMemo(() => {
    const map = {};
    templates.filter(t => t.is_active !== false).forEach(t => {
      (map[t.approval_type_code] = map[t.approval_type_code] || []).push(t);
    });
    return map;
  }, [templates]);

  return (
    <div className="space-y-6" data-testid="wae-chains">
      <div className="text-[13px] text-slate-500">
        Visualisation of every active approval chain across the property, grouped by approval type. Chains drive who signs off on what.
      </div>

      {types.length === 0 ? <EmptyState icon={Layers} msg="No approval types yet."/> : types.map(t => {
        const tpls = byType[t.code] || [];
        return (
          <div key={t.code} className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`wae-chain-type-${t.code}`}>
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-hocp-navy text-white flex items-center justify-center">
                <Layers className="w-4 h-4"/>
              </div>
              <div>
                <div className="font-display text-[14px] font-semibold text-hocp-navy">{t.name}</div>
                <div className="text-[11px] mono text-slate-400">{t.code}</div>
              </div>
              <div className="ml-auto text-[11.5px] text-slate-500">{tpls.length} template{tpls.length!==1?"s":""}</div>
            </div>
            {tpls.length === 0
              ? <div className="p-6 text-center text-[12.5px] text-slate-400">
                  No template configured — requests of this type will fail until a template exists.
                </div>
              : <div className="p-5 space-y-4">
                  {tpls.map(tpl => (
                    <div key={tpl.id} data-testid={`wae-chain-tpl-${tpl.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="text-[13px] font-medium text-hocp-navy">{tpl.name}</div>
                        {tpl.is_default && <span className="px-2 py-0.5 rounded-md bg-hocp-gold/20 text-hocp-navy text-[10px] font-medium">DEFAULT</span>}
                        {tpl.auto_approve && <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-[10px] font-medium">AUTO-APPROVE</span>}
                        <span className="ml-auto text-[10.5px] mono text-slate-400">{tpl.code}</span>
                      </div>
                      <div className="flex items-center gap-2 overflow-x-auto pb-2">
                        {(tpl.levels || []).map((lv, i) => (
                          <div key={lv.key} className="flex items-center gap-2">
                            <div className="min-w-[160px] p-3 rounded-xl bg-slate-50 border border-slate-200">
                              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">L{i + 1} · {lv.mode}</div>
                              <div className="font-medium text-[12.5px] text-hocp-navy truncate">{lv.name}</div>
                              <div className="text-[10.5px] text-slate-500 mt-1 line-clamp-2">
                                {(lv.approvers || []).map(a => `${KIND_LABEL[a.kind]}${a.ref ? ":"+a.ref : ""}`).join(", ") || "—"}
                              </div>
                              {(lv.when || []).length > 0 && (
                                <div className="text-[10px] mt-1 text-violet-600 truncate">
                                  IF {lv.when.map(c => `${c.field}${OP_LABEL[c.op]}${c.value}`).join(" & ")}
                                </div>
                              )}
                              <div className="text-[10px] text-slate-400 mt-0.5">
                                {lv.sla_hours || 24}h · {lv.quorum === "all" ? "all" : "any"}
                              </div>
                            </div>
                            {i < tpl.levels.length - 1 && <ArrowRight className="w-4 h-4 text-slate-300 shrink-0"/>}
                          </div>
                        ))}
                        <div className="min-w-[80px] p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-center shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto"/>
                          <div className="text-[10.5px] font-medium text-emerald-800 mt-1">Approved</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>}
          </div>
        );
      })}
    </div>
  );
}
